<template>
  <div class="container-fluid">

    <!-- Cargando -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box"><div class="loader-3"></div></div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="fetchEmpresa()">Reintentar</button>
      </div>
    </div>

    <!-- No encontrado -->
    <div v-else-if="!empresa" class="text-center py-5">
      <div class="alert alert-warning">Empresa no encontrada.</div>
      <button class="btn btn-outline-secondary" @click="$router.back()">
        <i class="fa fa-arrow-left me-1"></i> Regresar
      </button>
    </div>

    <div v-else>

      <!-- Hero banner -->
      <div class="row mt-3">
        <div class="col-12">
          <div class="card mb-0" style="overflow:hidden; border-radius: 15px 15px 0 0;">
            <img
              :src="empresa.banner || empresa.thumbnail_url || empresa.thumbnail || empresa.imagen || defaultImage"
              :alt="empresa.nombre || empresa.title"
              style="width:100%; height:150px; object-fit:cover; display:block;"
            />
          </div>
        </div>
      </div>

      <!-- Encabezado de empresa -->
      <div class="row">
        <div class="col-12">
          <div class="card" style="border-radius: 0 0 15px 15px;">
            <div class="card-body d-flex align-items-center gap-4 flex-wrap">
              <img
                v-if="empresa.logo || empresa.logo_url"
                :src="empresa.logo || empresa.logo_url"
                :alt="(empresa.nombre || empresa.title) + ' logo'"
                style="height:80px; width:auto; object-fit:contain; border-radius:8px; border:1px solid #e0e0e0; padding:6px; background:#fff;"
              />
              <div class="flex-grow-1">
                <h4 class="mb-1" style="color:#182e50;">{{ empresa.nombre || empresa.title }}</h4>
                <span v-if="empresa.sector?.nombre" class="badge me-1" style="background-color:#182e50;">{{ empresa.sector.nombre }}</span>
                <span v-if="empresa.tipo_institucion?.nombre" class="badge bg-success">{{ empresa.tipo_institucion.nombre }}</span>
              </div>
              <div class="d-flex gap-2 flex-wrap">
                <a v-if="empresa.web || empresa.website || empresa.url" :href="empresa.web || empresa.website || empresa.url" target="_blank" class="btn btn-outline-primary btn-sm">
                  <i class="fa fa-globe me-1"></i> Sitio web
                </a>
                <a v-if="empresa.email || empresa.correo" :href="'mailto:' + (empresa.email || empresa.correo)" class="btn btn-primary btn-sm">
                  <i class="fa fa-envelope me-1"></i> Contactar
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row starter-main">

        <!-- Columna principal -->
        <div class="col-lg-8 col-12">

          <!-- Descripción -->
          <div v-if="empresa.descripcion || empresa.intro || empresa.description || empresa.descripcionExtra || empresa.body" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-info-circle me-2"></i>Acerca de la empresa</h5>
            </div>
            <div class="card-body">
              <div v-if="empresa.descripcion || empresa.intro || empresa.description" class="rich-text mb-0" v-html="empresa.descripcion || empresa.intro || empresa.description"></div>
              <div v-if="empresa.descripcionExtra" class="rich-text mb-0" v-html="empresa.descripcionExtra"></div>
              <div v-if="empresa.body && !empresa.descripcion && !empresa.intro" class="rich-text mb-0" v-html="empresa.body"></div>
            </div>
          </div>

          <!-- Servicios ofrecidos -->
          <div v-if="serviciosOfrecidos.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-list-ul me-2"></i>Servicios ofrecidos</h5>
            </div>
            <div class="card-body">
              <ol class="ps-3 mb-0">
                <li v-for="(svc, i) in serviciosOfrecidos" :key="i" class="mb-2">{{ svc }}</li>
              </ol>
            </div>
          </div>

          <!-- Servicios -->
          <div v-if="servicios.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-leaf me-2"></i>Servicios verdes ofrecidos</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-sm-6 mb-3" v-for="(svc, i) in servicios" :key="i">
                  <div class="d-flex align-items-start gap-2">
                    <i class="fa fa-check-circle mt-1" style="color:#182e50; font-size:1.1rem;"></i>
                    <div>
                      <strong>{{ svc.titulo || svc.title || svc.nombre || svc.name }}</strong>
                      <p class="mb-0 text-muted small">{{ svc.descripcion || svc.description || svc.intro }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Casos de éxito -->
          <div v-if="casos.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-trophy me-2"></i>Casos de éxito</h5>
            </div>
            <div class="card-body">
              <ul class="list-unstyled mb-0" style="padding-left: 0px;">
                <li v-for="(caso, i) in casos" :key="i" class="mb-2 d-flex align-items-start">
                  <i class="fa fa-check-circle me-2 mt-1" style="color:#182e50; flex-shrink:0;"></i>
                  <span v-if="typeof caso === 'string'">{{ caso }}</span>
                  <span v-else>
                    <strong v-if="caso.titulo || caso.title" class="d-block" style="color:#182e50;">{{ caso.titulo || caso.title }}</strong>
                    <span class="text-muted small">{{ caso.descripcion || caso.description }}</span>
                    <span class="badge bg-success me-1" v-for="(tag, j) in (caso.tags || [])" :key="j">{{ tag }}</span>
                  </span>
                </li>
              </ul>
            </div>
          </div>

        </div>

        <!-- Columna lateral -->
        <div class="col-lg-4 col-12">

          <!-- Datos de contacto -->
          <div v-if="contacto || empresa.email || empresa.correo || empresa.correo_electronico || empresa.telefono || empresa.phone || empresa.direccion || empresa.address || empresa.sitio_web || empresa.web || empresa.website" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-address-card me-2"></i>Contacto</h5>
            </div>
            <div class="card-body" style="padding-left: 0px;">
              <ul class="list-unstyled mb-0">
                <li v-if="contacto && (contacto.nombre || contacto.name)" class="mb-2">
                  <i class="fa fa-user me-2" style="color:#182e50;"></i>
                  <strong>{{ contacto.nombre || contacto.name }}</strong>
                </li>
                <li v-if="contacto && (contacto.cargo || contacto.position)" class="mb-2">
                  <i class="fa fa-briefcase me-2" style="color:#182e50;"></i>{{ contacto.cargo || contacto.position }}
                </li>
                <li v-if="empresa.contacto_email" class="mb-2">
                  <i class="fa fa-envelope me-2" style="color:#182e50;"></i>
                  <a :href="'mailto:' + empresa.contacto_email">{{ empresa.contacto_email }}</a>
                </li>
                <li v-if="empresa.telefono || empresa.phone" class="mb-2">
                  <i class="fa fa-phone me-2" style="color:#182e50;"></i>{{ empresa.telefono || empresa.phone }}
                </li>
                <li v-if="empresa.direccion || empresa.municipio || empresa.estado?.nombre" class="mb-2">
                  <i class="fa fa-map-marker me-2" style="color:#182e50;"></i>
                  <span v-if="empresa.direccion">{{ empresa.direccion }}, </span>
                  <span v-if="empresa.municipio">{{ empresa.municipio }}, </span>
                  <span v-if="empresa.estado?.nombre">{{ empresa.estado.nombre }}</span>
                </li>
                <li v-if="empresa.sitio_web || empresa.web || empresa.website || empresa.url" class="mb-0">
                  <i class="fa fa-globe me-2" style="color:#182e50;"></i>
                  <a :href="empresa.sitio_web || empresa.web || empresa.website || empresa.url" target="_blank">{{ empresa.sitio_web || empresa.web || empresa.website || empresa.url }}</a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Indicadores -->
          <div v-if="indicadores.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-bar-chart me-2"></i>Indicadores clave</h5>
            </div>
            <div class="card-body">
              <div class="mb-3" v-for="(ind, i) in indicadores" :key="i">
                <div class="d-flex justify-content-between mb-1">
                  <small>{{ ind.label }}</small>
                  <small class="fw-bold">{{ ind.valor || ind.value }}</small>
                </div>
                <div class="progress" style="height:8px;">
                  <div class="progress-bar" :style="'width:' + (ind.pct || ind.percent || 0) + '%; background-color:#182e50;'"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Certificaciones -->
          <div v-if="certificaciones.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-certificate me-2"></i>Certificaciones</h5>
            </div>
            <div class="card-body">
              <span class="badge me-1 mb-1" style="margin-left:0px; background-color:#182e50; font-size:0.82rem;" v-for="(cert, i) in certificaciones" :key="i">{{ cert }}</span>
            </div>
          </div>

          <!-- Sectores atendidos -->
          <div v-if="sectoresAtendidos.length" class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-tags me-2"></i>Sectores atendidos</h5>
            </div>
            <div class="card-body">
              <span class="badge bg-secondary me-1 mb-1" v-for="(s, i) in sectoresAtendidos" :key="i">{{ s }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Botón regresar -->
      <div class="row mb-4">
        <div class="col-12">
          <button class="btn btn-outline-secondary" @click="$router.back()">
            <i class="fa fa-arrow-left me-1"></i> Regresar al directorio
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";
import defaultImageSrc from "@/assets/images/verde/ecobanner.webp";

export default {
  name: "DetalleServicio",
  data() {
    return {
      empresa: null,
      loading: true,
      error: null,
    };
  },
  computed: {
    defaultImage() {
      return defaultImageSrc;
    },
    contacto() {
      return this.empresa?.contacto || this.empresa?.contact || null;
    },
    serviciosOfrecidos() {
      const raw = this.empresa?.servicios_ofrecidos || "";
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
      return this.parseRichTextItems(raw);
    },
    servicios() {
      return this.empresa?.servicios || this.empresa?.services || this.empresa?.items || [];
    },
    casos() {
      const raw = this.empresa?.casos || this.empresa?.cases || this.empresa?.casos_exito || [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") return this.parseRichTextItems(raw);
      return [];
    },
    indicadores() {
      return this.empresa?.indicadores || this.empresa?.indicators || [];
    },
    certificaciones() {
      const raw = this.empresa?.certificaciones || this.empresa?.certifications || this.empresa?.certificates || [];
      if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
      if (typeof raw === "string") return this.parseRichTextItems(raw);
      return [];
    },
    sectoresAtendidos() {
      const raw = this.empresa?.sectoresAtendidos || this.empresa?.sectores || this.empresa?.sectors || this.empresa?.tags || [];
      const arr = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(",").map((s) => s.trim()) : [];
      return arr.map((s) => (typeof s === "object" ? s.nombre : s)).filter(Boolean);
    },
  },
  created() {
    this.fetchEmpresa();
  },
  methods: {
    parseRichTextItems(html) {
      if (!html) return [];
      const div = document.createElement("div");
      div.innerHTML = html;
      const lis = Array.from(div.querySelectorAll("li"));
      if (lis.length) return lis.map((el) => el.textContent.trim()).filter(Boolean);
      const ps = Array.from(div.querySelectorAll("p"));
      if (ps.length) return ps.map((el) => el.textContent.trim()).filter(Boolean);
      return div.textContent.split(",").map((s) => s.trim()).filter(Boolean);
    },
    async fetchEmpresa() {
      this.loading = true;
      this.error = null;
      try {
        const id = this.$route.params.id;
        const { data } = await api.get(`directorio/${id}`);
        this.empresa = data;
      } catch (err) {
        this.error = err;
        console.error("Error al cargar empresa:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.rich-text p { margin-bottom: 0.75rem; }
.rich-text p:last-child { margin-bottom: 0; }
.rich-text ul, .rich-text ol { padding-left: 1.25rem; margin-bottom: 0.75rem; }
.rich-text li { margin-bottom: 0.25rem; }
.rich-text h1, .rich-text h2, .rich-text h3, .rich-text h4 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #182e50;
}
.rich-text a { color: #09377c; }
.rich-text img { max-width: 100%; height: auto; border-radius: 6px; margin: 0.5rem 0; }
</style>
